import React, { useState, useEffect, useMemo, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { format } from 'date-fns'
import 'bulma/css/bulma.min.css'
import './Workouts.css'

import { useAuth } from './hooks/useAuth'
import { useSession } from './hooks/useSession'
import { supabase } from './lib/supabaseClient'

function Workouts() {
  const navigate = useNavigate()
  const { user, dbUser, dbUserError, loading: authLoading, isProvisioning, signInWithGoogle, signOut } = useAuth()
  const { sessions, loading: sessionsLoading, error, fetchAllSessions } = useSession()
  const isAdmin = dbUser?.is_admin === true

  // Track which sessions user is signed up for
  const [userSignups, setUserSignups] = useState({})
  // Track loading state per session
  const [signupLoading, setSignupLoading] = useState({})
  const [autoSignupEnabled, setAutoSignupEnabled] = useState(false)
  const [autoSignupLoading, setAutoSignupLoading] = useState(false)
  const [autoSignupUpdating, setAutoSignupUpdating] = useState(false)
  const [autoSignupError, setAutoSignupError] = useState(null)

  // Fetch all sessions after auth is fully initialized (even if user is null)
  useEffect(() => {
    // Fetch sessions once auth is loaded, regardless of user status
    if (authLoading === false) {
      fetchAllSessions()
    }
  }, [authLoading, fetchAllSessions])

  // Fetch user's signups
  const fetchUserSignups = useCallback(async () => {
    if (!dbUser) return
    
    try {
      const { data, error } = await supabase
        .from('SessionSignup')
        .select('session_id, status')
        .eq('user_id', dbUser.id)
        .eq('signup_role', 'practitioner')
        .in('status', ['registered', 'waitlist', 'attended'])
      
      if (error) throw error
      
      // Create a map of session_id -> signup status for quick lookup.
      const signupMap = {}
      data?.forEach(signup => {
        signupMap[signup.session_id] = signup
      })
      
      setUserSignups(signupMap)
    } catch (error) {
      console.error('Error fetching user signups:', error)
    }
  }, [dbUser])

  const fetchAutoSignupPreference = useCallback(async () => {
    if (!user || !dbUser || isAdmin) return

    setAutoSignupLoading(true)
    setAutoSignupError(null)

    try {
      const { data: sessionData } = await supabase.auth.getSession()
      const token = sessionData?.session?.access_token
      if (!token) throw new Error('Not authenticated. Please sign in again.')

      const response = await fetch('/api/auth/auto-signup', {
        headers: {
          Authorization: `Bearer ${token}`
        }
      })
      const result = await response.json()

      if (!response.ok) throw new Error(result.error || 'Failed to load auto-signup preference')
      setAutoSignupEnabled(result.autoSignupEnabled === true)
    } catch (preferenceError) {
      console.error('Error loading auto-signup preference:', preferenceError)
      setAutoSignupError(preferenceError.message)
    } finally {
      setAutoSignupLoading(false)
    }
  }, [user, dbUser, isAdmin])

  // Fetch signups when sessions load
  useEffect(() => {
    if (sessions.length > 0 && dbUser) {
      fetchUserSignups()
    }
  }, [sessions, dbUser, fetchUserSignups])

  useEffect(() => {
    fetchAutoSignupPreference()
  }, [fetchAutoSignupPreference])

  // Handle scroll to session on hash change
  useEffect(() => {
    const scrollToSession = () => {
      const hash = window.location.hash
      if (!hash || !hash.startsWith('#session-')) return

      const sessionId = hash.replace('#session-', '')
      const sessionElements = Array.from(document.querySelectorAll('[data-session-id]'))
      const element = sessionElements.find(candidate => (
        candidate.dataset.sessionId === sessionId && candidate.offsetParent !== null
      )) || document.getElementById(`session-${sessionId}`)
      
      if (element) {
        // Wait a bit for DOM to be ready, especially on initial load
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' })
          
          // Add highlight class
          element.classList.add('flash-highlight')
          
          // Remove highlight after animation
          setTimeout(() => {
            element.classList.remove('flash-highlight')
          }, 1200)
        }, 100)
      }
    }

    // Handle initial load
    scrollToSession()

    // Handle hash changes
    window.addEventListener('hashchange', scrollToSession)

    return () => {
      window.removeEventListener('hashchange', scrollToSession)
    }
  }, [sessions]) // Re-run when sessions load

  // State for sign-in prompt banner
  const [showSignInPrompt, setShowSignInPrompt] = useState(false)
  // State for debug info collapsible
  const [showDebug, setShowDebug] = useState(false)
  // State for admin email controls
  const [testEmail, setTestEmail] = useState('')
  const [isSendingEmails, setIsSendingEmails] = useState(false)

  // Signup handler function
  const handleSignUp = async (sessionId) => {
    // Only check for session (user), not dbUser
    // dbUser will be auto-created if missing
    if (!user) {
      // Show banner message prompting sign-in
      setShowSignInPrompt(true)
      return
    }
    
    // If user exists but dbUser is still loading or provisioning, wait
    if (!dbUser && (authLoading || isProvisioning)) {
      // Still loading/provisioning, don't proceed yet
      return
    }

    // CRITICAL: Check for dbUser.id before attempting signup
    if (!dbUser?.id) {
      alert('Your account is signed in but not registered yet. Please refresh in a few seconds or contact an admin.')
      return
    }

    setSignupLoading(prev => ({ ...prev, [sessionId]: true }))

    try {
      // Check if already signed up
      if (userSignups[sessionId]) {
        alert('You are already signed up for this session!')
        setSignupLoading(prev => ({ ...prev, [sessionId]: false }))
        return
      }

      // The RPC resolves the database user from auth.uid(), locks the session,
      // and assigns registered/waitlist status transactionally.
      const { data: signup, error } = await supabase
        .rpc('signup_current_user_for_session', { p_session_id: sessionId })

      if (error) throw error

      const status = signup?.status || 'registered'
      alert(status === 'waitlist' ? 'Session full — you joined the waitlist.' : 'Successfully registered!')

      setUserSignups(prev => ({ ...prev, [sessionId]: signup || { session_id: sessionId, status } }))
      
    } catch (error) {
      console.error('Signup error:', error)
      alert('Error signing up: ' + error.message)
    } finally {
      setSignupLoading(prev => ({ ...prev, [sessionId]: false }))
    }
  }

  const handleAutoSignupToggle = async () => {
    const nextEnabled = !autoSignupEnabled
    setAutoSignupUpdating(true)
    setAutoSignupError(null)

    try {
      const { data: sessionData } = await supabase.auth.getSession()
      const token = sessionData?.session?.access_token
      if (!token) throw new Error('Not authenticated. Please sign in again.')

      const response = await fetch('/api/auth/auto-signup', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ enabled: nextEnabled })
      })
      const result = await response.json()

      // The preference can be saved even if immediate processing later fails.
      if (typeof result.autoSignupEnabled === 'boolean') {
        setAutoSignupEnabled(result.autoSignupEnabled)
      }

      if (!response.ok) throw new Error(result.error || 'Failed to update auto-signup preference')

      setAutoSignupEnabled(result.autoSignupEnabled === true)
      if (result.autoSignupEnabled) await fetchUserSignups()
    } catch (preferenceError) {
      console.error('Error updating auto-signup preference:', preferenceError)
      setAutoSignupError(preferenceError.message)
    } finally {
      setAutoSignupUpdating(false)
    }
  }

  // Separate upcoming and past sessions, properly sorted
  const { upcomingSessions, pastSessions } = useMemo(() => {
    const now = new Date()
    const upcoming = []
    const past = []

    sessions.forEach(session => {
      // Handle session_date - could be datetime ISO string or date string
      let sessionDate
      if (typeof session.session_date === 'string') {
        // If it's an ISO datetime string, parse it directly
        if (session.session_date.includes('T')) {
          sessionDate = new Date(session.session_date)
        } else {
          // If it's just a date, combine with start_time if available
          if (session.start_time) {
            sessionDate = new Date(`${session.session_date}T${session.start_time}`)
          } else {
            sessionDate = new Date(session.session_date)
          }
        }
      } else {
        sessionDate = new Date(session.session_date)
      }

      if (sessionDate >= now) {
        upcoming.push(session)
      } else {
        past.push(session)
      }
    })

    // Sort upcoming by session_date ascending (soonest first)
    upcoming.sort((a, b) => {
      let dateA, dateB
      
      if (typeof a.session_date === 'string' && a.session_date.includes('T')) {
        dateA = new Date(a.session_date)
      } else if (a.start_time) {
        dateA = new Date(`${a.session_date}T${a.start_time}`)
      } else {
        dateA = new Date(a.session_date)
      }
      
      if (typeof b.session_date === 'string' && b.session_date.includes('T')) {
        dateB = new Date(b.session_date)
      } else if (b.start_time) {
        dateB = new Date(`${b.session_date}T${b.start_time}`)
      } else {
        dateB = new Date(b.session_date)
      }
      
      return dateA - dateB
    })

    // Sort past by session_date descending (most recent first)
    past.sort((a, b) => {
      let dateA, dateB
      
      if (typeof a.session_date === 'string' && a.session_date.includes('T')) {
        dateA = new Date(a.session_date)
      } else if (a.start_time) {
        dateA = new Date(`${a.session_date}T${a.start_time}`)
      } else {
        dateA = new Date(a.session_date)
      }
      
      if (typeof b.session_date === 'string' && b.session_date.includes('T')) {
        dateB = new Date(b.session_date)
      } else if (b.start_time) {
        dateB = new Date(`${b.session_date}T${b.start_time}`)
      } else {
        dateB = new Date(b.session_date)
      }
      
      return dateB - dateA
    })

    return { upcomingSessions: upcoming, pastSessions: past }
  }, [sessions])

  // Get formatted date from session
  const getFormattedDate = (session) => {
    let sessionDate
    if (typeof session.session_date === 'string' && session.session_date.includes('T')) {
      sessionDate = new Date(session.session_date)
    } else {
      sessionDate = new Date(session.session_date)
    }
    return format(sessionDate, 'MMM d, yyyy')
  }

  // Get time range display
  const getTimeRange = (session) => {
    let startDate
    
    // Determine the start date/time
    if (typeof session.session_date === 'string' && session.session_date.includes('T')) {
      // session_date is a full ISO datetime string
      startDate = new Date(session.session_date)
    } else if (session.start_time) {
      // session_date is just a date, combine with start_time
      startDate = new Date(`${session.session_date}T${session.start_time}`)
    } else if (typeof session.session_date === 'string') {
      // Just a date string, use start of day
      startDate = new Date(session.session_date)
    } else {
      return 'TBD'
    }
    
    // Calculate end time from start + duration_minutes
    if (session.duration_minutes) {
      const endDate = new Date(startDate.getTime() + session.duration_minutes * 60000)
      const startTime = format(startDate, 'HH:mm')
      const endTime = format(endDate, 'HH:mm')
      return `${startTime} - ${endTime}`
    }
    
    // Fallback: just show start time if no duration
    const startTime = format(startDate, 'HH:mm')
    return startTime
  }

  // Check if session is in the past
  const isSessionInPast = (session) => {
    let sessionDate
    if (typeof session.session_date === 'string' && session.session_date.includes('T')) {
      sessionDate = new Date(session.session_date)
    } else if (session.start_time) {
      sessionDate = new Date(`${session.session_date}T${session.start_time}`)
    } else {
      sessionDate = new Date(session.session_date)
    }
    return sessionDate < new Date()
  }

  // Truncate notes for display
  const truncateNotes = (notes, maxLength = 50) => {
    if (!notes) return '-'
    if (notes.length <= maxLength) return notes
    return notes.substring(0, maxLength) + '...'
  }

  const getMobileSessionStartDate = (session) => {
    let startDate

    if (typeof session.session_date === 'string' && session.session_date.includes('T')) {
      startDate = new Date(session.session_date)
    } else if (session.start_time) {
      startDate = new Date(`${session.session_date}T${session.start_time}`)
    } else if (typeof session.session_date === 'string') {
      startDate = new Date(session.session_date)
    } else {
      return null
    }

    return isNaN(startDate.getTime()) ? null : startDate
  }

  const getMobileFormattedDate = (session) => {
    const sessionDate = getMobileSessionStartDate(session)
    return sessionDate
      ? format(sessionDate, 'EEEE • MMMM d, yyyy').toUpperCase()
      : 'DATE TBD'
  }

  const getMobileTimeRange = (session) => {
    const startDate = getMobileSessionStartDate(session)

    if (!startDate) return 'Time TBD'

    const startTime = format(startDate, 'h:mm a')
    if (!session.duration_minutes) return startTime

    const endDate = new Date(startDate.getTime() + session.duration_minutes * 60000)
    return `${startTime} – ${format(endDate, 'h:mm a')}`
  }

  // Shared by the desktop table and mobile cards so signup behavior stays identical.
  const renderSignupAction = (session) => {
    if (isSessionInPast(session)) {
      return (
        <button
          className="button is-small is-light"
          disabled
          title="This session has already passed"
        >
          Past Session
        </button>
      )
    }

    if (userSignups[session.id]?.status === 'waitlist') {
      return <span className="tag is-warning is-medium">Waitlisted</span>
    }

    if (userSignups[session.id]?.status === 'attended') {
      return <span className="tag is-info is-medium">Attended</span>
    }

    if (userSignups[session.id]?.status === 'registered') {
      return <span className="tag is-success is-medium">Registered</span>
    }

    return (
      <div className="signup-action">
        <span className="signup-status-not-signed-up">Not signed up</span>
        <button
          className="button is-small is-primary"
          disabled={signupLoading[session.id]}
          onClick={() => handleSignUp(session.id)}
        >
          {signupLoading[session.id] ? 'Loading...' : 'Sign Up'}
        </button>
      </div>
    )
  }

  // Render table rows
  const renderTableRows = (sessionList) => {
    return sessionList.map((session, index) => (
      <tr 
        key={session.id || index}
        id={`session-${session.id}`}
        data-session-id={session.id}
      >
        <td>{getFormattedDate(session)}</td>
        <td>{getTimeRange(session)}</td>
        <td>{session.title || 'Untitled Session'}</td>
        <td>{session.location || '-'}</td>
        <td>
          <span className="tag is-info">{session.session_type || 'N/A'}</span>
        </td>
        <td>{session.coach_name || 'TBD'}</td>
        <td>
          <span 
            title={session.description || ''}
            className="has-text-grey"
            style={{ fontSize: '0.9em' }}
          >
            {truncateNotes(session.description)}
          </span>
        </td>
        <td>
          {renderSignupAction(session)}
        </td>
      </tr>
    ))
  }

  const renderMobileUpcomingSessions = (sessionList) => {
    return sessionList.map((session, index) => {
      const hasLongNotes = Boolean(session.description && session.description.length > 90)

      return (
        <article
          className="workouts-mobile-session-card"
          key={session.id || index}
          data-session-id={session.id}
        >
          <div className="workouts-mobile-session-heading">
            <p className="workouts-mobile-session-date">{getMobileFormattedDate(session)}</p>
            <span className="tag is-info">{session.session_type || 'Session'}</span>
          </div>

          <p className="workouts-mobile-session-time">{getMobileTimeRange(session)}</p>
          <h3 className="workouts-mobile-session-title">
            {session.title || 'Untitled Session'}
          </h3>
          <p className="workouts-mobile-session-location">
            <span aria-hidden="true">⌖</span> {session.location || 'Location TBD'}
          </p>

          <div className="workouts-mobile-session-registration">
            <div>
              <span className="workouts-mobile-session-meta-label">Capacity</span>
              <strong>
                {session.max_practitioners
                  ? `Up to ${session.max_practitioners} practitioners`
                  : 'Signup or waitlist'}
              </strong>
            </div>
            <div className="workouts-mobile-session-action">
              {renderSignupAction(session)}
            </div>
          </div>

          {!userSignups[session.id] && (
            <p className="workouts-mobile-session-waitlist-note">
              If the session is full, signup automatically joins the waitlist.
            </p>
          )}

          <div className="workouts-mobile-session-secondary">
            <p>
              <span className="workouts-mobile-session-meta-label">Coach</span>
              <strong>{session.coach_name || 'TBD'}</strong>
            </p>

            {session.description && (
              <div className="workouts-mobile-session-notes">
                <span className="workouts-mobile-session-meta-label">Notes</span>
                <p>{truncateNotes(session.description, 90)}</p>
                {hasLongNotes && (
                  <details>
                    <summary>More details</summary>
                    <p>{session.description}</p>
                  </details>
                )}
              </div>
            )}
          </div>
        </article>
      )
    })
  }

  const renderMobilePastSessions = (sessionList) => {
    return sessionList.map((session, index) => (
      <article
        className="workouts-mobile-past-session"
        key={session.id || index}
        data-session-id={session.id}
      >
        <div className="workouts-mobile-past-session-heading">
          <p>{getMobileFormattedDate(session)}</p>
          <span>Past</span>
        </div>
        <h3>{session.title || 'Untitled Session'}</h3>
        <p className="workouts-mobile-past-session-summary">
          {getMobileTimeRange(session)} · {session.location || 'Location TBD'}
        </p>
        <p className="workouts-mobile-past-session-meta">
          {session.session_type || 'Session'} · Coach: {session.coach_name || 'TBD'}
        </p>
        {session.description && (
          <p className="workouts-mobile-past-session-notes">
            {truncateNotes(session.description, 70)}
          </p>
        )}
      </article>
    ))
  }

  // Show loading state - differentiate between auth loading and sessions loading
  if (authLoading) {
    return (
      <div className="workouts-container">
        <div className="has-text-centered">
          <div className="spinner"></div>
          <p>Loading account...</p>
        </div>
      </div>
    )
  }

  // Show sessions loading state
  if (sessionsLoading) {
    return (
      <div className="workouts-container">
        <div className="has-text-centered">
          <div className="spinner"></div>
          <p>Loading sessions...</p>
        </div>
      </div>
    )
  }

  // Debug info (temporary)
  const debugInfo = user ? {
    email: user.email,
    id: user.id,
    isAdmin: isAdmin,
    autoSignupEnabled: autoSignupEnabled,
    dbUser: dbUser,
    dbUserError: dbUserError
  } : null

  // Handle navigation to /workouts/new with route existence check
  const handleAddWorkout = () => {
    // Check if route exists by trying to navigate
    // Note: In React Router v6, we can't easily check route existence,
    // but we'll log a warning if navigation fails
    try {
      navigate('/workouts/new')
    } catch (error) {
      console.warn('[Workouts] Warning: Route /workouts/new may not exist:', error)
      // Still attempt navigation - React Router will handle it
      navigate('/workouts/new')
    }
  }

  // Handle admin-triggered email sending
  const handleSendEmailsNow = async () => {
    if (!isAdmin) {
      alert('Admin access required')
      return
    }

    const confirmMessage = testEmail
      ? `Send today's emails to test email: ${testEmail}? (No flags will be updated)`
      : "Send today's emails now? This can re-send even after 9am."

    if (!window.confirm(confirmMessage)) {
      return
    }

    setIsSendingEmails(true)

    try {
      // Get Supabase session token
      const { data: sessionData } = await supabase.auth.getSession()
      const token = sessionData?.session?.access_token

      if (!token) {
        throw new Error('Not authenticated. Please sign in.')
      }

      // Call admin endpoint
      const response = await fetch('/api/admin/run-emails', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          force: true,
          testEmail: testEmail || undefined,
          daysAhead: 2
        })
      })

      const result = await response.json()

      if (!response.ok) {
        throw new Error(result.error || 'Failed to send emails')
      }

      // Show success message with counts
      const signupsCount = result.signups?.emailsSent || 0
      const remindersCount = result.reminders?.emailsSent || 0
      const summariesCount = result.summaries?.emailsSent || 0
      const total = signupsCount + remindersCount + summariesCount

      alert(
        `Emails sent successfully!\n\n` +
        `Signups: ${signupsCount}\n` +
        `Reminders: ${remindersCount}\n` +
        `Summaries: ${summariesCount}\n` +
        `Total: ${total}`
      )

      // Clear test email if used
      if (testEmail) {
        setTestEmail('')
      }
    } catch (error) {
      console.error('Error sending emails:', error)
      alert(`Error: ${error.message}`)
    } finally {
      setIsSendingEmails(false)
    }
  }

  return (
    <div className="workouts-container">
      <div className="level">
        <div className="level-left">
          <div>
            <h1 className="title">Sessions</h1>
            {user && (
              <p className="subtitle">
                Welcome, {user.user_metadata?.full_name || user.email}
              </p>
            )}
          </div>
        </div>
        <div className="level-right">
          {isAdmin && (
            <button
              className="button is-primary is-large"
              onClick={handleAddWorkout}
            >
              Add Workout
            </button>
          )}
          {isAdmin && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginRight: '10px' }}>
              <input
                type="email"
                className="input"
                placeholder="Test Email (optional)"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                style={{ width: '200px' }}
              />
              <button
                className="button is-info"
                onClick={handleSendEmailsNow}
                disabled={isSendingEmails}
              >
                {isSendingEmails ? 'Sending...' : "Send Today's Emails Now"}
              </button>
            </div>
          )}
          {user && (
            <button
              className="button is-light"
              onClick={signOut}
            >
              Sign Out
            </button>
          )}
        </div>
      </div>

      {user && dbUser && !isAdmin && (
        <div className={`auto-signup-box ${autoSignupEnabled ? 'is-active' : ''}`}>
          <div className="auto-signup-copy">
            <div className="auto-signup-heading-row">
              <h2 className="title is-4">3-Week Auto-Signup</h2>
              {autoSignupEnabled && (
                <span className="tag is-success is-medium">Auto-Signup Active ✓</span>
              )}
            </div>
            <p>Automatically register me as a practitioner for active Muay Thai sessions up to 3 weeks in advance.</p>
            {autoSignupError && <p className="help is-danger">{autoSignupError}</p>}
          </div>
          <button
            className={`button ${autoSignupEnabled ? 'is-light' : 'is-primary'}`}
            onClick={handleAutoSignupToggle}
            disabled={autoSignupLoading || autoSignupUpdating}
          >
            {autoSignupLoading
              ? 'Loading...'
              : autoSignupUpdating
                ? 'Saving...'
                : autoSignupEnabled
                  ? 'Disable Auto-Signup'
                  : 'Enable Auto-Signup'}
          </button>
        </div>
      )}

      {/* Debug info (collapsible) */}
      {dbUser?.is_admin === true && debugInfo && (
        <div className="debug-container">
          <div 
            className="debug-header"
            onClick={() => setShowDebug(prev => !prev)}
          >
            <span className={`debug-toggle ${showDebug ? "open" : ""}`}>
              {"<"}
            </span>
            <span className="debug-title">Debug Info</span>
          </div>

          <div className={`debug-content ${showDebug ? "open" : ""}`}>
            {showDebug && (
              <pre>
                {JSON.stringify(debugInfo, null, 2)}
              </pre>
            )}
          </div>
        </div>
      )}

      {/* Sign-in prompt - show when NOT signed in */}
      {!authLoading && !user && (
        <div className="notification is-info">
          <p>Please sign in to register for sessions.</p>
          <button
            className="button is-primary"
            onClick={signInWithGoogle}
          >
            Sign in with Google
          </button>
        </div>
      )}

      {/* Provisioning message - show ONLY while isProvisioning is true */}
      {!authLoading && user && isProvisioning && (
        <div className="notification is-warning">
          <div className="level">
            <div className="level-left">
              <div>
                <p><strong>Finishing account setup...</strong></p>
                <p>Please wait a moment while we set up your account.</p>
              </div>
            </div>
            <div className="level-right">
              <div className="spinner" style={{ margin: 0 }}></div>
            </div>
          </div>
        </div>
      )}

      {/* Edge case: Provisioning finished but still no dbUser */}
      {!authLoading && user && !isProvisioning && !dbUser && (
        <div className="notification is-danger">
          <p><strong>Account setup failed</strong></p>
          <p>Your account is signed in but not registered yet. Please refresh the page or contact an admin.</p>
        </div>
      )}

      {/* Sign-in prompt banner when clicking Sign Up while logged out */}
      {showSignInPrompt && (
        <div className="notification is-warning">
          <button className="delete" onClick={() => setShowSignInPrompt(false)}></button>
          <p><strong>Sign in to register</strong></p>
          <p>Please sign in to register for sessions.</p>
          <button
            className="button is-primary"
            onClick={() => {
              setShowSignInPrompt(false)
              signInWithGoogle()
            }}
          >
            Sign in with Google
          </button>
        </div>
      )}

      {/* Error display */}
      {error && (
        <div className="notification is-danger">
          <button className="delete" onClick={() => window.location.reload()}></button>
          Error: {error}
        </div>
      )}

      {/* Sessions Table */}
      <div className="sessions-table-container">
        {/* Upcoming Sessions */}
        {upcomingSessions.length > 0 && (
          <div className="sessions-section">
            <div className="level">
              <div className="level-left">
                <h2 className="title is-4">Upcoming Sessions</h2>
              </div>
            </div>
            <div className="table-container workouts-desktop-session-table">
              <table className="table is-striped is-hoverable is-fullwidth">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Time</th>
                    <th>Title</th>
                    <th>Location</th>
                    <th>Session Type</th>
                    <th>Coach</th>
                    <th>Notes</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {renderTableRows(upcomingSessions)}
                </tbody>
              </table>
            </div>
            <div className="workouts-mobile-session-list">
              {renderMobileUpcomingSessions(upcomingSessions)}
            </div>
          </div>
        )}

        {/* Past Sessions */}
        {pastSessions.length > 0 && (
          <div className="sessions-section">
            <h2 className="title is-4">Past Sessions</h2>
            <div className="table-container workouts-desktop-session-table">
              <table className="table is-striped is-hoverable is-fullwidth">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Time</th>
                    <th>Title</th>
                    <th>Location</th>
                    <th>Session Type</th>
                    <th>Coach</th>
                    <th>Notes</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {renderTableRows(pastSessions)}
                </tbody>
              </table>
            </div>
            <div className="workouts-mobile-past-list">
              {renderMobilePastSessions(pastSessions)}
            </div>
          </div>
        )}

        {/* Empty state */}
        {upcomingSessions.length === 0 && pastSessions.length === 0 && !sessionsLoading && (
          <div className="box has-text-centered" style={{ padding: '3rem' }}>
            <p className="title is-4 has-text-grey">No sessions available</p>
            <p className="subtitle is-6 has-text-grey-light">
              {isAdmin
                ? 'Get started by creating your first session.'
                : 'Check back later for upcoming training sessions.'}
            </p>
          </div>
        )}
      </div>
    </div>
  )
}

export default Workouts
